/**
 * "Relazioni" section of the semantics editor (L2 knowledge graph, ADR-0026 §4).
 *
 * Lists every relationship that touches THIS dataset (on either end), with the
 * direction rendered relative to it, and add / edit / delete. The visual graph
 * of the whole instance lives at /datasets/graph.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Network, Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { RelationshipDialog } from "@/components/datasets/RelationshipDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CARDINALITY_GLYPH,
  relationshipsApi,
  type Relationship,
} from "@/lib/api-relationships";

export function RelationshipsSection({ datasetId }: { datasetId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Relationship | null>(null);

  const relsQ = useQuery({
    queryKey: ["relationships", { dataset_id: datasetId }],
    queryFn: () => relationshipsApi.list({ dataset_id: datasetId }),
    enabled: Boolean(datasetId),
  });

  const remove = useMutation({
    mutationFn: relationshipsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["relationships"] }),
  });

  const rels = relsQ.data ?? [];

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (r: Relationship) => {
    setEditing(r);
    setDialogOpen(true);
  };

  return (
    <section className="overflow-hidden rounded-lg border border-border/60">
      <div className="flex items-center gap-2 border-b border-border/60 bg-surface px-3 py-2">
        <Network className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-sm font-semibold">{t("relationships.title", "Relazioni")}</span>
        <span className="text-xs font-normal text-muted-foreground">({rels.length})</span>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/datasets/graph">
              <Network className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("relationships.viewGraph", "Vista grafo")}
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("relationships.add", "Relazione")}
          </Button>
        </div>
      </div>

      {rels.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {t("relationships.empty", "Nessuna relazione per questo dataset.")}
        </p>
      ) : (
        <ul className="divide-y divide-border/60">
          {rels.map((r) => {
            // Render the edge relative to THIS dataset (outgoing vs incoming).
            const outgoing = r.from_dataset_id === datasetId;
            const near = outgoing
              ? { field: r.from_field }
              : { field: r.to_field };
            const far = outgoing
              ? { ds: r.to_dataset_name, field: r.to_field }
              : { ds: r.from_dataset_name, field: r.from_field };
            return (
              <li key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs">{near.field}</span>
                <Badge tone="neutral" className="text-[10px]">
                  {CARDINALITY_GLYPH[r.cardinality]}
                </Badge>
                <ArrowRight
                  className={outgoing ? "h-3.5 w-3.5 text-muted-foreground" : "h-3.5 w-3.5 rotate-180 text-muted-foreground"}
                  strokeWidth={1.5}
                />
                <span className="truncate">
                  <span className="text-muted-foreground">{far.ds}</span>
                  <span className="font-mono text-xs">.{far.field}</span>
                </span>
                <span className="truncate text-xs text-muted-foreground">· {r.name}</span>
                <div className="ml-auto flex items-center gap-1">
                  <Button variant="ghost" size="icon" aria-label={t("common.edit", "Modifica")} onClick={() => openEdit(r)}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.delete", "Elimina")}
                    onClick={() => {
                      if (confirm(t("relationships.deleteConfirm", `Eliminare la relazione "${r.name}"?`)))
                        remove.mutate(r.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <RelationshipDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        fromDatasetId={datasetId}
        editing={editing}
      />
    </section>
  );
}
